import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  race,
  switchMap,
  take,
  tap,
  timer
} from 'rxjs';

import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { PLAN_EDIT_RATE_LIMITED } from '@core/models/plan-edit.model';
import { PlanEditSocketService } from '@core/services/plan-edit/plan-edit-socket.service';
import * as AuthActions from '@core/store/auth/auth.actions';
import { selectIsFeatureActive } from '@core/store/feature-flags/feature-flags.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';

import { PlanEditActions, PlanWatchActions } from './plan-edit.actions';
import { selectPlanWatch } from './plan-edit.selectors';

/** How long a saved overlay waits for a data view's refetch when none may be mounted. */
export const RELEASE_FALLBACK_MS = 10_000;
export const RATE_LIMIT_RETRY_MS = 2_000;

@Injectable()
export class PlanWatchEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly socket = inject(PlanEditSocketService);

  readonly followPlanInView$ = createEffect(() =>
    combineLatest([
      this.store.select(selectPlan).pipe(map((plan) => plan?.id ?? null)),
      this.store.select(selectIsFeatureActive(KNOWN_FEATURE_FLAGS.ENABLE_LIVE_PLAN_EDIT))
    ]).pipe(
      map(([planId, enabled]) => (enabled ? planId : null)),
      distinctUntilChanged(),
      map((planId) =>
        planId === null
          ? PlanWatchActions.watchStopped()
          : PlanWatchActions.watchRequested({ planId })
      )
    )
  );

  readonly sendWatch$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(PlanWatchActions.watchRequested),
          tap(({ planId }) => this.socket.watch(planId))
        ),
        this.actions$.pipe(
          ofType(PlanWatchActions.watchStopped),
          tap(() => this.socket.unwatch())
        )
      ),
    { dispatch: false }
  );

  /**
   * Every trigger yields a fresh snapshot of the plan watched. A `watch-started` always asks for one:
   * the re-watch is what names a new session's flavours, and what makes GBE publish its patches at all.
   */
  readonly rewatch$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(PlanWatchActions.socketConnected),
        map(() => null)
      ),
      this.actions$.pipe(
        ofType(PlanWatchActions.watchStarted),
        map(({ planId }) => planId)
      ),
      this.actions$.pipe(
        ofType(PlanEditActions.planUpdatedRemotely),
        concatLatestFrom(() => this.store.select(selectPlanWatch)),
        filter(([, watch]) => watch.phase === 'held-unavailable'),
        map(([{ planId }]) => planId)
      ),
      // Released once the data view shows the saved rows, so the stored plan never flashes; the
      // snapshot that replaces the overlay is relative to the saved plan.
      this.actions$.pipe(
        ofType(PlanWatchActions.watchSaved),
        switchMap(({ planId }) =>
          race(
            this.actions$.pipe(
              ofType(PlanWatchActions.overlayReleased),
              filter((released) => released.planId === planId),
              take(1)
            ),
            timer(RELEASE_FALLBACK_MS)
          ).pipe(map(() => planId))
        )
      ),
      this.actions$.pipe(
        ofType(PlanWatchActions.watchRejected),
        filter(({ errorCode }) => errorCode === PLAN_EDIT_RATE_LIMITED),
        switchMap(() => timer(RATE_LIMIT_RETRY_MS)),
        map(() => null)
      )
    ).pipe(
      concatLatestFrom(() => this.store.select(selectPlanWatch)),
      filter(
        ([named, watch]) =>
          watch.planId !== null && (named === null || named === watch.planId)
      ),
      map(([, watch]) => PlanWatchActions.watchRequested({ planId: watch.planId! }))
    )
  );

  /** `plan:updated` skips internal planning spaces; the watch's own save event does not. */
  readonly refreshOnSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanWatchActions.watchSaved),
      map(({ planId, planDataRevision }) =>
        PlanEditActions.planUpdatedRemotely({ planId, planDataRevision })
      )
    )
  );

  readonly closeOnLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => this.socket.close())
      ),
    { dispatch: false }
  );
}
